import { useState } from "react";
import { Plus, StickyNote } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";
import { useVocabularyNotes } from "@/hooks/use-vocabulary-notes";
import { VocabularyNoteCard } from "@/components/vocabulary/VocabularyNoteCard";
import { SessionNoteInput } from "@/components/session/SessionNoteInput";
import { SectionLabel } from "@/components/vocabulary/practice/WordMeaning";

interface WordNotesProps {
  /** The word to show notes for (matched by word string, case-insensitive). Ignored for variant="list". */
  word?: string;
  /**
   * "panel" — editable cards for a single word + empty/loading states (Detail dialog).
   * "inline" — compact read-only list for a single word; renders nothing when none (Practice reveal).
   * "list"  — list of ALL the user's vocabulary notes, compact rows (Dictionary "Notes" tab).
   */
  variant?: "panel" | "inline" | "list";
  /** Shown in the "panel" variant when no word is being viewed yet. */
  emptyWordHint?: string;
  /** Allow creating a new note for `word` (panel variant only, e.g. the vocabulary preview). */
  allowCreate?: boolean;
  /** Compact mode: lighter rendering for the flashcard back (no Card wrapper, no empty-state illustration). */
  compact?: boolean;
  className?: string;
}

const EmptyState = ({ title, hint, className }: { title: string; hint: string; className?: string }) => (
  <div className={cn("flex flex-col items-center justify-center text-center py-12", className)}>
    <div className="w-14 h-14 rounded-2xl bg-primary/5 flex items-center justify-center mb-3">
      <StickyNote className="h-7 w-7 text-primary" />
    </div>
    <h3 className="font-bold text-sm mb-1">{title}</h3>
    <p className="text-xs text-muted-foreground max-w-[240px] leading-relaxed">{hint}</p>
  </div>
);

const LoadingState = ({ className }: { className?: string }) => (
  <div className={cn("space-y-2", className)}>
    {[1, 2].map((i) => (
      <Skeleton key={i} className="h-24 w-full rounded-2xl" />
    ))}
  </div>
);

/**
 * Displays the user's vocabulary notes (and, with allowCreate, lets them add a
 * note for the current word). Notes are also created during the topic practice
 * session (see use-session-notes); standalone notes store their word in `title`.
 */
export const WordNotes = ({
  word = "",
  variant = "panel",
  emptyWordHint,
  allowCreate = false,
  compact = false,
  className,
}: WordNotesProps) => {
  const { t } = useTranslation();
  const trimmed = word.trim();
  // "list" is the whole notes archive; every other variant is about exactly one
  // word, so the query is scoped to it. It used to pull the archive (up to 200
  // note bodies) per word and discard all but the matching few.
  const isArchive = variant === "list";
  const { notes, isLoading, createNote, updateNote, deleteNote } = useVocabularyNotes({
    word: isArchive ? undefined : trimmed || undefined,
    enabled: isArchive || trimmed.length > 0,
  });
  const [composing, setComposing] = useState(false);

  const matches =
    isArchive
      ? notes
      : trimmed
        ? notes.filter((n) => (n.word ?? "").trim().toLowerCase() === trimmed.toLowerCase())
        : [];

  // ----- inline (read-only, practice reveal): show nothing unless there are notes -----
  if (variant === "inline") {
    if (isLoading || !trimmed || matches.length === 0) return null;
    return (
      <div className={className}>
        <SectionLabel>{t("pages.vocabularyNotes.myNotes", "My notes")}</SectionLabel>
        <div className="space-y-2">
          {matches.map((note) => (
            <div
              key={note.id}
              className="border-l-2 border-amber-400/40 pl-2.5 text-sm leading-snug text-muted-foreground prose prose-sm dark:prose-invert max-w-none prose-p:my-0 prose-p:leading-snug"
              dangerouslySetInnerHTML={{ __html: note.content }}
            />
          ))}
        </div>
      </div>
    );
  }

  if (isLoading) return <LoadingState className={className} />;

  // ----- list (all notes, compact read-only rows: dictionary Notes tab) -----
  if (variant === "list") {
    if (matches.length === 0) {
      return (
        <EmptyState
          title={t("pages.vocabularyNotes.emptyTitle", "No notes yet")}
          hint={t(
            "pages.vocabularyNotes.practiceEmptyHint",
            "Notes are added from the sticky-note icon on a vocabulary word during practice."
          )}
          className={className}
        />
      );
    }
    return (
      <div className={className}>
        <p className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider px-2.5 py-2">
          {t("pages.vocabularyNotes.noteCount", "{{count}} notes", { count: matches.length })}
        </p>
        <div className="space-y-0.5">
          {matches.map((note) => (
            <div
              key={note.id}
              className="px-2.5 py-2 rounded-lg hover:bg-primary/5 transition-colors"
            >
              <div className="min-w-0">
                <p className="text-sm font-medium truncate" title={note.word ?? undefined}>
                  {note.word ?? t("pages.vocabularyNotes.unknownWord", "Unknown word")}
                </p>
                <div
                  className="text-xs text-muted-foreground line-clamp-2 break-words prose prose-sm dark:prose-invert max-w-none prose-p:my-0 prose-p:leading-snug"
                  dangerouslySetInnerHTML={{ __html: note.content }}
                />
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  // ----- panel (single word, editable: detail dialog) -----
  if (!trimmed) {
    return (
      <EmptyState
        title={t("pages.vocabularyNotes.wordNotesTitle", "Word Notes")}
        hint={
          emptyWordHint ??
          t(
            "pages.vocabularyNotes.wordNotesHint",
            "Look up a word to see notes you took on it during practice"
          )
        }
        className={className}
      />
    );
  }

  // ----- compact panel (flashcard back): at most one inline note + SessionNoteInput -----
  if (compact) {
    const note = matches[0];
    const hasNote = Boolean(note);

    return (
      <div className={cn("space-y-2", className)}>
        {hasNote ? (
          <SessionNoteInput
            value={note!.content}
            onSave={(content) => updateNote.mutate({ id: note!.id, content })}
            onDelete={() => deleteNote.mutate(note!.id)}
          />
        ) : composing ? (
          <div className="rounded-xl border border-border/50 bg-muted/10 p-2">
            <SessionNoteInput
              value=""
              onSave={(content) => {
                createNote.mutate({ word: trimmed, content });
                setComposing(false);
              }}
              onDelete={() => setComposing(false)}
              isSaving={createNote.isPending}
            />
          </div>
        ) : allowCreate ? (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => setComposing(true)}
            className="h-7 gap-1 rounded-full text-xs text-muted-foreground hover:text-foreground"
          >
            <Plus className="w-3 h-3" />
            {t("pages.vocabularyNotes.addNote", "Add note")}
          </Button>
        ) : null}
      </div>
    );
  }

  const addButton = allowCreate && !composing && (
    <Button
      type="button"
      variant="outline"
      size="sm"
      onClick={() => setComposing(true)}
      className="gap-1.5 rounded-full text-xs h-8"
    >
      <Plus className="w-3.5 h-3.5" />
      {t("pages.vocabularyNotes.addNote", "Add note")}
    </Button>
  );

  const composer = allowCreate && composing && (
    <div className="rounded-2xl border border-stone-200/60 dark:border-stone-700/60 bg-card p-3">
      <SessionNoteInput
        value=""
        onSave={(content) => {
          createNote.mutate({ word: trimmed, content });
          setComposing(false);
        }}
        onDelete={() => setComposing(false)}
        isSaving={createNote.isPending}
      />
    </div>
  );

  if (matches.length === 0) {
    return (
      <div className={cn("space-y-3", className)}>
        {composer}
        {!composing && (
          <div className="flex flex-col items-center justify-center text-center py-8">
            <div className="w-14 h-14 rounded-2xl bg-primary/5 flex items-center justify-center mb-3">
              <StickyNote className="h-7 w-7 text-primary" />
            </div>
            <h3 className="font-bold text-sm mb-1">
              {t("pages.vocabularyNotes.emptyTitle", "No notes yet")}
            </h3>
            <p className="text-xs text-muted-foreground max-w-[240px] leading-relaxed mb-3">
              {allowCreate
                ? t("pages.vocabularyNotes.addNoteForWord", 'Add a note or mnemonic for "{{word}}".', {
                    word: trimmed,
                  })
                : t(
                    "pages.vocabularyNotes.practiceEmptyHint",
                    "Notes are added from the sticky-note icon on a vocabulary word during practice."
                  )}
            </p>
            {addButton}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className={cn("space-y-2", className)}>
      <p className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider">
        {t("pages.vocabularyNotes.noteCount", "{{count}} notes", { count: matches.length })}
      </p>
      {matches.map((note) => (
        <VocabularyNoteCard
          key={note.id}
          note={note}
          showWord={false}
          onSaveContent={(content) => updateNote.mutate({ id: note.id, content })}
          onDelete={() => deleteNote.mutate(note.id)}
        />
      ))}
    </div>
  );
};
